import React from "react";
import {
    Trophy,
    Shield,
    CalendarDays,
} from "lucide-react";

const H2hDataShow = ({
    h2hData,
}) => {

    // ============================
    // SAFE DATA
    // ============================

    const h2h =
        h2hData
            ?.firstTeam_VS_secondTeam ||
        [];

    const firstTeamResults =
        h2hData?.firstTeam_lastResults ||
        [];

    const secondTeamResults =
        h2hData?.secondTeam_lastResults ||
        [];

    // ============================
    // TEAM INFO
    // ============================

    const homeTeam =
        h2hData?.match_hometeam_name;

    const awayTeam =
        h2hData?.match_awayteam_name;

    // ============================
    // H2H STATS
    // ============================

    let homeWins = 0;
    let awayWins = 0;
    let draws = 0;

    h2h.forEach((match) => {

        const homeScore =
            Number(
                match?.match_hometeam_score
            );

        const awayScore =
            Number(
                match?.match_awayteam_score
            );

        const isHomeTeam =
            match?.match_hometeam_name
                ?.toLowerCase()
                .includes(
                    homeTeam?.toLowerCase()
                );

        const team1Score =
            isHomeTeam
                ? homeScore
                : awayScore;

        const team2Score =
            isHomeTeam
                ? awayScore
                : homeScore;

        if (
            team1Score > team2Score
        ) {
            homeWins++;
        } else if (
            team2Score > team1Score
        ) {
            awayWins++;
        } else {
            draws++;
        }
    });

    // ============================
    // RESULT COLOR
    // ============================

    const getResult = (
        match,
        teamName
    ) => {

        const isHome =
            match?.match_hometeam_name
                ?.toLowerCase()
                .includes(
                    teamName?.toLowerCase()
                );

        const teamScore =
            Number(
                isHome
                    ? match?.match_hometeam_score
                    : match?.match_awayteam_score
            );

        const opponentScore =
            Number(
                isHome
                    ? match?.match_awayteam_score
                    : match?.match_hometeam_score
            );

        if (
            teamScore > opponentScore
        ) {
            return {
                text: "W",
                class:
                    "bg-green-500/20 text-green-500 border-green-500/30",
            };
        }

        if (
            teamScore < opponentScore
        ) {
            return {
                text: "L",
                class:
                    "bg-red-500/20 text-red-500 border-red-500/30",
            };
        }

        return {
            text: "D",
            class:
                "bg-yellow-500/20 text-yellow-500 border-yellow-500/30",
        };
    };

    // ============================
    // MATCH CARD
    // ============================

    const MatchCard = ({
        match,
    }) => {

        const isLive =
            match?.match_live ===
            "1";

        return (

            <div
                className="
                bg-white
                dark:bg-[#161616]
                border
                border-gray-200
                dark:border-gray-800
                rounded-2xl
                p-4
                transition-all
                hover:shadow-xl
                hover:-translate-y-1
                "
            >

                {/* TOP */}

                <div
                    className="
                    flex
                    items-center
                    justify-between
                    mb-4
                    "
                >

                    <div
                        className="
                        flex
                        items-center
                        gap-2
                        "
                    >

                        <img
                            src={
                                match?.league_logo
                            }
                            alt=""
                            className="
                            w-7
                            h-7
                            rounded-full
                            object-cover
                            "
                        />

                        <div>

                            <p
                                className="
                                text-xs
                                font-semibold
                                text-gray-900
                                dark:text-white
                                "
                            >

                                {
                                    match?.league_name
                                }

                            </p>

                            <p
                                className="
                                text-[11px]
                                text-gray-500
                                "
                            >

                                {
                                    match?.country_name ||
                                    "League Match"
                                }

                            </p>

                        </div>

                    </div>

                    {isLive ? (

                        <span
                            className="
                            inline-flex
                            items-center
                            gap-1
                            px-3
                            py-1
                            rounded-full
                            bg-red-500/20
                            text-red-500
                            text-xs
                            font-bold
                            animate-pulse
                            "
                        >

                            <span
                                className="
                                w-2
                                h-2
                                rounded-full
                                bg-red-500
                                "
                            />

                            LIVE

                        </span>

                    ) : (

                        <span
                            className="
                            px-3
                            py-1
                            rounded-full
                            bg-gray-100
                            dark:bg-[#222]
                            text-xs
                            text-gray-600
                            dark:text-gray-300
                            "
                        >

                            {
                                match?.match_status
                            }

                        </span>
                    )}

                </div>

                {/* TEAM */}

                <div
                    className="
                    flex
                    items-center
                    justify-between
                    gap-3
                    "
                >

                    {/* HOME */}

                    <div
                        className="
                        flex-1
                        flex
                        flex-col
                        items-center
                        text-center
                        "
                    >

                        <div
                            className="
                            w-16
                            h-16
                            p-2
                            rounded-2xl
                            bg-gray-50
                            dark:bg-[#1f1f1f]
                            border
                            border-gray-200
                            dark:border-gray-700
                            "
                        >

                            <img
                                src={
                                    match?.team_home_badge
                                }
                                alt=""
                                className="
                                w-full
                                h-full
                                object-contain
                                "
                            />

                        </div>

                        <h3
                            className="
                            mt-2
                            text-sm
                            font-semibold
                            text-gray-900
                            dark:text-white
                            "
                        >

                            {
                                match?.match_hometeam_name
                            }

                        </h3>

                    </div>

                    {/* SCORE */}

                    <div
                        className="
                        min-w-[100px]
                        text-center
                        "
                    >

                        <div
                            className="
                            text-3xl
                            md:text-4xl
                            font-black
                            bg-gradient-to-r
                            from-blue-500
                            to-orange-500
                            bg-clip-text
                            text-transparent
                            "
                        >

                            {
                                match?.match_hometeam_score
                            }

                            <span
                                className="
                                mx-2
                                text-gray-400
                                "
                            >

                                -

                            </span>

                            {
                                match?.match_awayteam_score
                            }

                        </div>

                        <p
                            className="
                            mt-1
                            text-xs
                            text-gray-500
                            "
                        >

                            HT{" "}
                            {
                                match?.match_hometeam_halftime_score
                            }

                            -

                            {
                                match?.match_awayteam_halftime_score
                            }

                        </p>

                    </div>

                    {/* AWAY */}

                    <div
                        className="
                        flex-1
                        flex
                        flex-col
                        items-center
                        text-center
                        "
                    >

                        <div
                            className="
                            w-16
                            h-16
                            p-2
                            rounded-2xl
                            bg-gray-50
                            dark:bg-[#1f1f1f]
                            border
                            border-gray-200
                            dark:border-gray-700
                            "
                        >

                            <img
                                src={
                                    match?.team_away_badge
                                }
                                alt=""
                                className="
                                w-full
                                h-full
                                object-contain
                                "
                            />

                        </div>

                        <h3
                            className="
                            mt-2
                            text-sm
                            font-semibold
                            text-gray-900
                            dark:text-white
                            "
                        >

                            {
                                match?.match_awayteam_name
                            }

                        </h3>

                    </div>

                </div>

                {/* FOOTER */}

                <div
                    className="
                    mt-4
                    pt-3
                    border-t
                    border-gray-200
                    dark:border-gray-800
                    flex
                    items-center
                    justify-between
                    text-xs
                    text-gray-500
                    "
                >

                    <div
                        className="
                        flex
                        items-center
                        gap-1
                        "
                    >

                        <CalendarDays
                            size={14}
                        />

                        {
                            match?.match_date
                        }

                    </div>

                    <div>

                        {
                            match?.match_time
                        }

                    </div>

                </div>

            </div>
        );
    };

    // ============================
    // RESULT FORM
    // ============================

    const TeamForm = ({
        data,
        teamName,
        title,
    }) => {

        return (

            <div
                className="
                bg-white
                dark:bg-[#161616]
                border
                border-gray-200
                dark:border-gray-800
                rounded-2xl
                p-5
                "
            >

                <div
                    className="
                    flex
                    items-center
                    justify-between
                    mb-4
                    "
                >

                    <h3
                        className="
                        text-lg
                        font-bold
                        text-gray-900
                        dark:text-white
                        "
                    >

                        {title}

                    </h3>

                    <div
                        className="
                        flex
                        items-center
                        gap-2
                        "
                    >

                        {data
                            ?.slice(0, 5)
                            ?.map(
                                (
                                    item,
                                    index
                                ) => {

                                    const result =
                                        getResult(
                                            item,
                                            teamName
                                        );

                                    return (

                                        <div
                                            key={
                                                index
                                            }
                                            className={`
                                            w-8
                                            h-8
                                            rounded-full
                                            border
                                            flex
                                            items-center
                                            justify-center
                                            text-xs
                                            font-bold
                                            ${result.class}
                                            `}
                                        >

                                            {
                                                result.text
                                            }

                                        </div>
                                    );
                                }
                            )}

                    </div>

                </div>

                <div className="space-y-3">

                    {data
                        ?.slice(0, 5)
                        ?.map(
                            (
                                item,
                                index
                            ) => (

                                <div
                                    key={
                                        index
                                    }
                                    className="
                                    flex
                                    items-center
                                    justify-between
                                    gap-3
                                    p-3
                                    rounded-xl
                                    bg-gray-50
                                    dark:bg-[#1f1f1f]
                                    "
                                >

                                    <div>

                                        <p
                                            className="
                                            text-sm
                                            font-semibold
                                            text-gray-900
                                            dark:text-white
                                            "
                                        >

                                            {
                                                item?.match_hometeam_name
                                            }

                                            {" "}
                                            vs{" "}

                                            {
                                                item?.match_awayteam_name
                                            }

                                        </p>

                                        <p
                                            className="
                                            text-xs
                                            text-gray-500
                                            "
                                        >

                                            {
                                                item?.league_name
                                            }

                                        </p>

                                    </div>

                                    <div
                                        className="
                                        text-right
                                        "
                                    >

                                        <p
                                            className="
                                            text-lg
                                            font-bold
                                            text-blue-500
                                            "
                                        >

                                            {
                                                item?.match_hometeam_score
                                            }

                                            -

                                            {
                                                item?.match_awayteam_score
                                            }

                                        </p>

                                        <p
                                            className="
                                            text-xs
                                            text-gray-500
                                            "
                                        >

                                            {
                                                item?.match_date
                                            }

                                        </p>

                                    </div>

                                </div>
                            )
                        )}

                </div>

            </div>
        );
    };

    return (

        <div className="space-y-6">

            {/* HEADER */}

            <div
                className="
                relative
                overflow-hidden
                rounded-3xl
                bg-gradient-to-r
                from-blue-600
                via-indigo-600
                to-orange-500
                p-6
                text-white
                "
            >

                <div
                    className="
                    absolute
                    inset-0
                    opacity-10
                    bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]
                    "
                />

                <div
                    className="
                    relative
                    z-10
                    flex
                    flex-col
                    md:flex-row
                    items-center
                    justify-between
                    gap-6
                    "
                >

                    {/* HOME */}

                    <div
                        className="
                        flex
                        items-center
                        gap-4
                        "
                    >

                        <img
                            src={
                                h2hData?.team_home_badge
                            }
                            alt=""
                            className="
                            w-20
                            h-20
                            rounded-2xl
                            bg-white
                            p-2
                            "
                        />

                        <div>

                            <h2
                                className="
                                text-2xl
                                font-black
                                "
                            >

                                {homeTeam}

                            </h2>

                            <p className="text-sm text-white/80">
                                Formation{" "}
                                {
                                    h2hData?.match_hometeam_system
                                }
                            </p>

                        </div>

                    </div>

                    {/* H2H */}

                    <div className="text-center">

                        <div
                            className="
                            inline-flex
                            items-center
                            gap-2
                            bg-white/10
                            backdrop-blur-md
                            px-5
                            py-2
                            rounded-full
                            mb-3
                            "
                        >

                            <Trophy
                                size={18}
                            />

                            Head To Head

                        </div>

                        <div
                            className="
                            flex
                            items-center
                            justify-center
                            gap-6
                            "
                        >

                            <div>

                                <h3
                                    className="
                                    text-3xl
                                    font-black
                                    "
                                >

                                    {
                                        homeWins
                                    }

                                </h3>

                                <p className="text-xs">
                                    Wins
                                </p>

                            </div>

                            <div>

                                <h3
                                    className="
                                    text-3xl
                                    font-black
                                    "
                                >

                                    {
                                        draws
                                    }

                                </h3>

                                <p className="text-xs">
                                    Draws
                                </p>

                            </div>

                            <div>

                                <h3
                                    className="
                                    text-3xl
                                    font-black
                                    "
                                >

                                    {
                                        awayWins
                                    }

                                </h3>

                                <p className="text-xs">
                                    Wins
                                </p>

                            </div>

                        </div>

                    </div>

                    {/* AWAY */}

                    <div
                        className="
                        flex
                        items-center
                        gap-4
                        "
                    >

                        <div className="text-right">

                            <h2
                                className="
                                text-2xl
                                font-black
                                "
                            >

                                {awayTeam}

                            </h2>

                            <p className="text-sm text-white/80">
                                Formation{" "}
                                {
                                    h2hData?.match_awayteam_system
                                }
                            </p>

                        </div>

                        <img
                            src={
                                h2hData?.team_away_badge
                            }
                            alt=""
                            className="
                            w-20
                            h-20
                            rounded-2xl
                            bg-white
                            p-2
                            "
                        />

                    </div>

                </div>

            </div>

            {/* H2H MATCHES */}

            <div>

                <div
                    className="
                    flex
                    items-center
                    gap-2
                    mb-4
                    "
                >

                    <Shield
                        className="
                        text-blue-500
                        "
                    />

                    <h2
                        className="
                        text-2xl
                        font-bold
                        text-gray-900
                        dark:text-white
                        "
                    >

                        Previous Meetings

                    </h2>

                </div>

                <div
                    className="
                    grid
                    grid-cols-1
                    lg:grid-cols-2
                    gap-5
                    "
                >

                    {h2h?.map(
                        (
                            match,
                            index
                        ) => (

                            <MatchCard
                                key={index}
                                match={match}
                            />
                        )
                    )}

                </div>

            </div>

            {/* TEAM FORMS */}

            <div
                className="
                grid
                grid-cols-1
                lg:grid-cols-2
                gap-5
                "
            >

                <TeamForm
                    data={
                        firstTeamResults
                    }
                    teamName={
                        homeTeam
                    }
                    title={`${homeTeam} Recent Form`}
                />

                <TeamForm
                    data={
                        secondTeamResults
                    }
                    teamName={
                        awayTeam
                    }
                    title={`${awayTeam} Recent Form`}
                />

            </div>

        </div>
    );
};

export default H2hDataShow;